import { InboundDeliveryOrderDto } from '@nest/domain-entities-metadata-generated';
import { Skeleton } from '@nest/uikit-react';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useNavigate } from 'react-router';
import { useTerminalClient } from '../client-rpc-router';
import { TerminalScreen } from '../components/TerminalScreen';
import { useNumberParam } from '../helpers';

export default function InboundQueueDetailPage() {
	const client = useTerminalClient();
	const queueId = useNumberParam('queueId');

	const { data: queue } = useQuery({
		queryKey: ['inbound-queue', queueId],
		queryFn: () => client.inboundDeliveryOrderProcess.getInboundQueueById.execute({ id: queueId! })
	});

	const { data: inboundDeliveryOrders } = useQuery({
		queryKey: ['inbound-delivery-orders-by-queue', queueId],
		queryFn: () => client.inboundDeliveryOrderProcess.getAllInboundDeliveryOrdersByQueueQuery.execute({ queueId: queueId! }),
		enabled: !!queueId
	});

	return (
		<TerminalScreen showBackButton topBar={<div>{queue?.name}</div>}>
			<div className="h-full overflow-auto">
				{inboundDeliveryOrders ? (
					<div className="divide-y divide-border">
						{inboundDeliveryOrders.map(inboundDeliveryOrder => (
							<InboundDeliveryOrderRow key={inboundDeliveryOrder.id} inboundDeliveryOrder={inboundDeliveryOrder} />
						))}
					</div>
				) : (
					<div className="divide-y divide-border">
						<Skeleton className="h-16 w-full" />
						<Skeleton className="h-16 w-full" />
						<Skeleton className="h-16 w-full" />
						<Skeleton className="h-16 w-full" />
						<Skeleton className="h-16 w-full" />
					</div>
				)}
			</div>
		</TerminalScreen>
	);
}

const InboundDeliveryOrderRow = React.memo(({ inboundDeliveryOrder }: { inboundDeliveryOrder: InboundDeliveryOrderDto }) => {
	const navigate = useNavigate();

	const handleOpen = () => {
		navigate(`/inbound/delivery/orders/${inboundDeliveryOrder.id}`);
	};

	const formattedRequestDateTime = React.useMemo(() => {
		const date = inboundDeliveryOrder.requestDate ? new Date(inboundDeliveryOrder.requestDate) : undefined;

		if (!date) {
			return '';
		}

		return new Intl.DateTimeFormat('cs-CZ', {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit'
		}).format(date);
	}, [inboundDeliveryOrder.requestDate]);

	const contractorName = inboundDeliveryOrder.contractorSnapshot?.name ?? inboundDeliveryOrder.contractor?.name ?? '';

	const totalPieces = React.useMemo(() => {
		const lines = inboundDeliveryOrder.lines ?? [];

		const sumOfPieceQuantities = lines.reduce((sum, line) => {
			if (line.unit === 'Piece') {
				return sum + (line.quantity ?? 0);
			}

			return sum;
		}, 0);

		if (sumOfPieceQuantities > 0) {
			return sumOfPieceQuantities;
		}

		return lines.length ?? 0;
	}, [inboundDeliveryOrder.lines]);

	const isHighlighted = inboundDeliveryOrder.state === 'InProgress';

	return (
		<button
			type="button"
			onClick={handleOpen}
			className={`w-full px-4 py-3 transition-colors hover:bg-slate-50 ${isHighlighted ? 'bg-yellow-50 hover:bg-yellow-100' : 'bg-white hover:bg-slate-50'}`}
		>
			<div className="flex items-start justify-between">
				<div className="min-w-0 flex-1">
					<div className="text-xs text-slate-500 font-medium">{formattedRequestDateTime}</div>

					<div className="mt-1 truncate text-base font-medium text-slate-900">{contractorName}</div>
				</div>

				<div className="flex shrink-0 flex-col items-end gap-1">
					<div className="text-base font-semibold text-blue-600">{inboundDeliveryOrder.documentNumber}</div>

					{inboundDeliveryOrder.foreignOrderNumber ? <div className="text-sm font-medium text-purple-600">{inboundDeliveryOrder.foreignOrderNumber}</div> : null}

					<div className="mt-1 inline-flex h-6 items-center justify-center rounded-md bg-slate-100 px-2 text-sm font-semibold text-slate-700">{totalPieces}ks</div>
				</div>
			</div>
		</button>
	);
});
